import type { Identifier } from "ra-core";

import type { LeadSource } from "../types";

/**
 * Almikia Epic E2b (TASK-022): pure helpers behind the direccion-only
 * "Orígenes de leads" screen. The rules mirror the FakeRest guard and the SQL
 * guard (required non-blank name <= 60 chars, case-insensitive unique name).
 */

export const LEAD_SOURCE_NAME_REQUIRED = "Escribe el nombre del origen";
export const LEAD_SOURCE_NAME_TOO_LONG =
  "El nombre del origen no puede superar 60 caracteres";
export const LEAD_SOURCE_NAME_DUPLICATE = "Ya existe un origen con ese nombre";
export const LEAD_SOURCE_COLOR_INVALID = "Elige un color válido";
export const LEAD_SOURCE_GENERIC_ERROR =
  "No se pudo guardar el cambio. Inténtalo de nuevo.";

export const LEAD_SOURCE_NAME_MAX_LENGTH = 60;

/** Design-system palette (docs/almikia/design-system.md): institutional + semantic colors. */
export const LEAD_SOURCE_PALETTE: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: "#272C57", label: "Azul marino" },
  { value: "#B42E77", label: "Magenta" },
  { value: "#D14E2F", label: "Naranja" },
  { value: "#F1D432", label: "Amarillo" },
  { value: "#3BB273", label: "Verde" },
  { value: "#D6452B", label: "Rojo" },
];

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

export interface LeadSourceFormValues {
  name: string;
  color: string;
  meta_mapping: string;
  active: boolean;
}

export interface LeadSourceFormErrors {
  name?: string;
  color?: string;
}

const normalizeName = (name: string): string => name.trim().toLowerCase();

/**
 * Validates the form. `existing` is the full catalogue; `selfId` excludes the
 * edited row from the duplicate check.
 */
export function validateLeadSourceForm(
  values: Pick<LeadSourceFormValues, "name" | "color">,
  existing: ReadonlyArray<Pick<LeadSource, "id" | "name">>,
  selfId: Identifier | undefined,
): LeadSourceFormErrors {
  const errors: LeadSourceFormErrors = {};
  const name = values.name.trim();
  if (name.length === 0) {
    errors.name = LEAD_SOURCE_NAME_REQUIRED;
  } else if (name.length > LEAD_SOURCE_NAME_MAX_LENGTH) {
    errors.name = LEAD_SOURCE_NAME_TOO_LONG;
  } else if (
    existing.some(
      (source) =>
        source.id !== selfId &&
        normalizeName(source.name) === normalizeName(name),
    )
  ) {
    errors.name = LEAD_SOURCE_NAME_DUPLICATE;
  }
  if (!HEX_COLOR_PATTERN.test(values.color)) {
    errors.color = LEAD_SOURCE_COLOR_INVALID;
  }
  return errors;
}

/** The record sent to the data provider: trimmed name, empty mapping stored as null. */
export function buildLeadSourcePayload(
  values: LeadSourceFormValues,
): Pick<LeadSource, "name" | "color" | "meta_mapping" | "active"> {
  const mapping = values.meta_mapping.trim();
  return {
    name: values.name.trim(),
    color: values.color,
    meta_mapping: mapping.length > 0 ? mapping : null,
    active: values.active,
  };
}

/** Active sources first, then inactive ones, each alphabetical. */
export function orderLeadSourcesForList(
  sources: readonly LeadSource[],
): LeadSource[] {
  return sources.slice().sort((a, b) => {
    if (a.active !== b.active) return a.active ? -1 : 1;
    return a.name.localeCompare(b.name, "es");
  });
}

/** Server rejections are already Spanish sentences; pass them through. */
export function toLeadSourceErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : undefined;
  return message || LEAD_SOURCE_GENERIC_ERROR;
}
